import { useMemo, useCallback, memo } from 'react';
import { Eye, Pencil, Trash2, Check, Plus, User } from 'lucide-react';
import { getQuestionTypeLabel } from '../../utils/questionUtils';
import { useUser } from '../../context/UserContext';
import ExercicePreview from './ExercicePreview';
import { FORMAT_CONFIG, DEFAULT_FORMAT, getArticle, formatDateCourte, normaliserQuestionBanque } from './formatConfig';

/**
 * Carte d'une question de banque créée par un prof — même apparence que la
 * carte exerciseur (ExerciseurCard) : en-tête avec le format et l'auteur,
 * aperçu COMPLET de l'exercice (ExercicePreview, mêmes renderers que
 * l'élève), actions directes (œil / crayon / corbeille), bouton d'ajout.
 *
 * Différences assumées avec l'exerciseur : une question = une carte, pas de
 * formats ni de variantes (⟳), pas de « Personnaliser » (brouillon) : une
 * question manuelle se consomme telle quelle. Le PROPRIÉTAIRE (professeur ===
 * prof connecté) peut la Modifier (édition persistée, `onEditQuestion`) et la
 * Supprimer ; les autres n'ont que l'Aperçu élève et l'ajout. Le back refuse
 * de toute façon PUT/DELETE d'un non-propriétaire.
 * L'ajout COCHE la question (selectedIds, pas de doublon en base).
 */
const BanqueQuestionCard = ({
    question, selectable, selected, onToggleSelect, onPreview, onEditQuestion, onDeleteQuestion,
}) => {
    const base = useMemo(() => normaliserQuestionBanque(question), [question]);
    const typeLabel = getQuestionTypeLabel(base);
    const cfg = FORMAT_CONFIG[typeLabel] || DEFAULT_FORMAT;

    // Auteur et propriété : la banque ne renvoie que l'id du professeur ; le
    // nom vient de la liste des profs de l'établissement (UserContext). Repli
    // générique si le prof n'y figure pas (autre matière en établissement GAR,
    // compte supprimé) ou hors provider (tests).
    const { user, etablissementData } = useUser() || {};
    const estProprietaire = base?.professeur != null
        && user?.role === 'professeur' && user?.id === base.professeur;
    const auteur = useMemo(() => {
        if (estProprietaire) return 'vous';
        const p = etablissementData?.professeurs?.find((x) => x.id === base?.professeur);
        if (!p) return null;
        return `${p.prenom || ''} ${p.nom || ''}`.trim() || null;
    }, [base, etablissementData, estProprietaire]);
    const libelleAuteur = auteur ? `Créée par ${auteur}` : 'Question du prof';

    const handleAjouter = useCallback((e) => {
        e.stopPropagation();
        onToggleSelect?.(base.id);
    }, [base, onToggleSelect]);

    if (!base) return null;

    const peutModifier = estProprietaire && Boolean(onEditQuestion);
    const peutSupprimer = estProprietaire && Boolean(onDeleteQuestion);

    return (
        <div className={`bg-white rounded-[18px] overflow-hidden break-inside-avoid transition-all duration-[250ms] ${
            selected
                ? 'border-[1.5px] border-emerald-500 shadow-[0_0_0_3px_rgba(16,185,129,0.12),0_2px_8px_rgba(0,0,0,0.04)]'
                : 'border-[1.5px] border-gray-200 shadow-sm hover:shadow-md'
        }`}>
            {/* ── Header : format + auteur + menu ⋮ ── */}
            <div className="pt-5 px-[22px] pb-3.5 flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium border-2 ${cfg.border} ${cfg.bg} ${cfg.text}`}>
                            <span className="text-sm">{cfg.icon}</span>
                            {typeLabel}
                        </span>
                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-violet-600" title={libelleAuteur}>
                            <User size={12} /> {libelleAuteur}
                        </span>
                    </div>
                    <span className="block text-[10px] text-slate-400 mt-1">
                        {formatDateCourte(base.date_creation)}
                        {base.competence_titre ? ` · ${base.competence_titre}` : ''}
                    </span>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                    {selected && (
                        <span className="text-xs font-medium px-2 py-0.5 rounded-md bg-green-50 text-green-600">Ajoutée</span>
                    )}
                    {/* Actions directes (un clic) : aperçu pour tous, modifier et
                        supprimer pour le propriétaire */}
                    {onPreview && (
                        <button
                            onClick={(e) => { e.stopPropagation(); onPreview(base); }}
                            title="Aperçu élève"
                            aria-label="Aperçu élève"
                            className="w-7 h-7 rounded-md hover:bg-gray-100 flex items-center justify-center transition text-gray-400 hover:text-gray-700"
                        >
                            <Eye size={15} />
                        </button>
                    )}
                    {peutModifier && (
                        <button
                            onClick={(e) => { e.stopPropagation(); onEditQuestion(base); }}
                            title="Modifier cette question"
                            aria-label="Modifier cette question"
                            className="w-7 h-7 rounded-md hover:bg-gray-100 flex items-center justify-center transition text-gray-400 hover:text-gray-700"
                        >
                            <Pencil size={15} />
                        </button>
                    )}
                    {peutSupprimer && (
                        <button
                            onClick={(e) => { e.stopPropagation(); onDeleteQuestion(base.id); }}
                            title="Supprimer cette question"
                            aria-label="Supprimer cette question"
                            className="w-7 h-7 rounded-md hover:bg-red-50 flex items-center justify-center transition text-gray-300 hover:text-red-500"
                        >
                            <Trash2 size={15} />
                        </button>
                    )}
                </div>
            </div>

            {/* ── Aperçu complet ── */}
            <div className="mx-4 rounded-[14px] overflow-hidden border-[1.5px] border-gray-200 bg-[#fafbfc]">
                <div className="px-4 pt-4 pb-2 relative">
                    <div style={{ zoom: 0.88 }} className="text-[13px] overflow-hidden">
                        <ExercicePreview question={base} />
                    </div>
                </div>
            </div>

            {/* ── Action : ajouter (coche) / retirer ── */}
            {selectable ? (
                <div className="pt-3 px-[22px] pb-4">
                    <button
                        onClick={handleAjouter}
                        className={`w-full py-2 rounded-lg text-xs font-semibold transition flex items-center justify-center gap-1.5 ${
                            selected
                                ? 'bg-emerald-500 hover:bg-emerald-600 text-white'
                                : 'bg-blue-600 hover:bg-blue-700 text-white'
                        }`}
                    >
                        {selected ? <Check size={14} /> : <Plus size={14} />}
                        {selected ? 'Ajoutée · Retirer' : `Ajouter ${getArticle(typeLabel)} ${typeLabel}`}
                    </button>
                </div>
            ) : (
                <div className="h-4" />
            )}
        </div>
    );
};

export default memo(BanqueQuestionCard);
